import React, { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import CompanyIcon from "../../assets/Icons/CompanyIcon.svg";
import CustomAlert from "../../helpers/CustomAlert";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import { format, parse } from "date-fns";
import { CircularProgress } from "@mui/material";
import CampaignAnalytics from "./CampaignAnalytics";

const RcsNewModal = ({ show, handleClose }) => {
  const loggedInUserName = sessionStorage.getItem("username");
  const userRole = sessionStorage.getItem("userRole");
  const [loading, setLoading] = useState(false);
  const [rcsDashboardData, setRCSDashboardData] = useState();
  const isCustomer = userRole === "CUST";

  // useEffect(() => {
  //   fetchRCSDashboardData();
  // }, []);

  const fetchRCSDashboardData = async (reqBody) => {
    setLoading(true);
    try {
      const payload = {
        username: loggedInUserName,
        ...reqBody,
      };

      const response = await axiosPostApi(`${apiList.RCS_DASHBOARD}`, payload);
      const data = response?.data;

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setRCSDashboardData(data);
        return data;
      } else {
        CustomAlert({
          title: "Failed",
          text:
            response?.data?.message ||
            "There was an issue processing the request.",
          icon: "error",
          confirmButtonText: "Retry",
        });
        return null;
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const memoizedCallApi = useCallback(fetchRCSDashboardData, [
    loggedInUserName,
  ]);

  if (!show) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-[1031]">
      <div className="w-full max-w-[80%] max-h-[90vh] rounded-[24px] border border-[#D2D2E2] bg-white shadow-lg flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-[#F1FFFF] px-8 py-[32px] flex justify-between items-start rounded-t-2xl">
          {/* Left section: Icon + Text */}
          <div className="flex items-start gap-4">
            <img
              src={CompanyIcon}
              alt="Company Icon"
              className="w-[90px] h-[90px] object-contain"
            />

            {/* Text block */}
            <div className="mt-4">
              {isCustomer ? (
                <p className="text-[#145165] font-bold text-[18px]">
                  RCS Dashboard
                </p>
              ) : (
                <>
                  <h5 className="font-poppins font-medium text-[18px] leading-[100%] text-[#495057] -mt-1 mb-1">
                    Company Onboarded For RCS Services Till Now
                  </h5>

                  <p className="text-[#145165] font-bold text-[18px]">
                    {rcsDashboardData?.totalCompanies} Companies
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={handleClose}
            className="text-gray-600 hover:text-gray-800 text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 px-6 py-6  overflow-auto">
          <CampaignAnalytics
            rcsDashboardData={rcsDashboardData}
            callApi={memoizedCallApi}
            loading={loading}
          />
          {loading ? (
            <div className="flex justify-center items-center h-[300px]">
              <CircularProgress />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 px-6 text-sm bg-[#F1FFFF] ">
                {/* Tab*/}
                <div
                  className="w-[256px] h-[40px] flex items-center px-2.5 py-1.5
             border-t border-l border-r border-[#D2D2E2]
             rounded-t-[5px] bg-white whitespace-nowrap"
                >
                  <span className="font-poppins font-medium text-[14px] text-[#495057]">
                    {isCustomer
                      ? "Onboarded Company Name"
                      : "Last Five Onboarded Company"}
                  </span>
                </div>

                <div className="flex-1 flex items-center gap-3 relative">
                  {/* underline pushed down */}
                  <div className="absolute left-[-15px] right-0 bottom-[-10px] h-[1px] bg-[#D2D2E2]" />

                  {/* Label */}
                  <span className="font-poppins font-medium text-[14px] text-[#495057] whitespace-nowrap">
                    Total RCS Count ({rcsDashboardData?.totalRcsCount}):
                  </span>

                  {/* Today badge */}
                  <span className="px-2 py-[2px] rounded-[5px] bg-[#FFF7E9] h-[25px] flex items-center whitespace-nowrap">
                    <span className="font-poppins font-medium text-[14px] text-[#FFC107]">
                      Today : {rcsDashboardData?.today}
                    </span>
                  </span>

                  {/* Divider */}
                  <div className="w-[2px] h-[12px] bg-[#495057] opacity-100"></div>

                  {/* Last 3 months badge */}
                  <span className="px-2 py-[2px] rounded-[5px] bg-[#DBF4FFA8] h-[25px] flex items-center whitespace-nowrap">
                    <span className="font-poppins font-medium text-[14px] text-[#007BFF]">
                      Last Three months : {rcsDashboardData?.lastThreeMonths}
                    </span>
                  </span>

                  {/* Divider */}
                  <div className="w-[2px] h-[12px] bg-[#495057] opacity-100"></div>

                  {/* Last 6 months badge */}
                  <span className="px-2 py-[2px] rounded-[5px] bg-[#D1FADF] h-[25px] flex items-center whitespace-nowrap">
                    <span className="font-poppins font-medium text-[14px] text-[#15803D]">
                      Last Six months : {rcsDashboardData?.lastSixMonths}
                    </span>
                  </span>
                </div>
              </div>

              <div className="w-full max-h-[500px] overflow-auto rounded-b-[24px]">
                <table className="min-w-full text-sm text-left border-collapse">
                  <thead className="bg-[#F4FBFB] text-gray-600">
                    <tr>
                      <th className="px-4 py-2 font-medium relative">
                        Sr No
                        <div className="absolute top-4 bottom-4 right-0 w-px bg-[#D2D2E2]"></div>
                      </th>

                      <th className="px-4 py-2 font-medium relative">
                        Company Name
                        <div className="absolute top-4 bottom-4 right-0 w-px bg-[#D2D2E2]"></div>
                      </th>

                      <th className="px-4 py-2 font-medium relative">
                        Created <br /> Date-Time
                        <div className="absolute top-4 bottom-4 right-0 w-px bg-[#D2D2E2]"></div>
                      </th>

                      <th className="px-4 py-2 font-medium relative">
                        Used Qty
                        <div className="absolute top-4 bottom-4 right-0 w-px bg-[#D2D2E2]"></div>
                      </th>

                      <th className="px-4 py-2 font-medium relative">
                        Failed Qty
                        <div className="absolute top-4 bottom-4 right-0 w-px bg-[#D2D2E2]"></div>
                      </th>

                      <th className="px-4 py-2 font-medium relative">
                        Total Qty
                        <div className="absolute top-4 bottom-4 right-0 w-px bg-[#D2D2E2]"></div>
                      </th>

                      <th className="px-4 py-2 font-medium relative">
                        Balance Qty
                        <div className="absolute top-4 bottom-4 right-0 w-px bg-[#D2D2E2]"></div>
                      </th>

                      <th className="px-4 py-2 font-medium">
                        Last RCS <br /> Date-Time
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rcsDashboardData?.data.map((row, index) => (
                      <tr key={row?.id} className="hover:bg-gray-50">
                        <td className="h-[32px] px-4 py-1 relative">
                          {index + 1}
                          <div className="absolute top-4 bottom-4 right-0 w-px bg-[#D2D2E2]"></div>
                        </td>
                        <td className="px-4 py-2 relative">
                          {row?.companyName}
                          <div className="absolute top-4 bottom-4 right-0 w-px bg-[#D2D2E2]"></div>
                        </td>
                        <td className="px-4 py-2 relative">
                          {
                            row?.createdDateTime
                              ? (() => {
                                  try {
                                    // Parse the given string to a Date object
                                    const parsedDate = parse(
                                      row?.createdDateTime,
                                      "dd-MM-yyyy HH:mm:ss",
                                      new Date()
                                    );

                                    return (
                                      <>
                                        {format(parsedDate, "dd/MM/yyyy")}
                                        <br key={`txn-${index}`} />
                                        {format(parsedDate, "hh:mm:ss a")}
                                      </>
                                    );
                                  } catch (e) {
                                    return "-"; // fallback if parsing fails
                                  }
                                })()
                              : "-" // fallback if null
                          }

                          <div className="absolute top-4 bottom-4 right-0 w-px bg-[#D2D2E2]"></div>
                        </td>
                        <td className="px-4 py-2 relative">
                          {row?.usedQty} qty
                          <div className="absolute top-4 bottom-4 right-0 w-px bg-[#D2D2E2]"></div>
                        </td>
                        <td className="px-4 py-2 relative">
                          {row?.failedQty} qty
                          <div className="absolute top-4 bottom-4 right-0 w-px bg-[#D2D2E2]"></div>
                        </td>
                        <td className="px-4 py-2 relative">
                          {row?.totalQty} qty
                          <div className="absolute top-4 bottom-4 right-0 w-px bg-[#D2D2E2]"></div>
                        </td>
                        <td className="px-4 py-2 relative">
                          {row?.balanceQty} qty
                          <div className="absolute top-4 bottom-4 right-0 w-px bg-[#D2D2E2]"></div>
                        </td>
                        <td className="px-4 py-2">
                          {row?.createdDateTime
                            ? (() => {
                                try {
                                  const parsedDate = parse(
                                    row?.lastRcsDateTime,
                                    "dd-MM-yyyy HH:mm:ss",
                                    new Date()
                                  );

                                  return (
                                    <>
                                      {format(parsedDate, "dd/MM/yyyy")}
                                      <br key={`txn-${index}`} />
                                      {format(parsedDate, "hh:mm:ss a")}
                                    </>
                                  );
                                } catch (e) {
                                  return "-";
                                }
                              })()
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RcsNewModal;
